// Topbar — breadcrumb + lang toggle + notifications bell + UserMenu.
//
// Per shell-strings.md §"Topbar":
//   - Breadcrumb (left): mono 10px ink3 +0.08em, " / " separator,
//     uppercase. Caller provides ordered segments.
//   - Lang toggle (EN/FR) — drives the i18n provider.
//   - Notifications bell + unread-count badge (panel itself out of scope here).
//   - UserMenu trigger (separate component).

import { BellIcon, LangToggle } from '@gc-coach/design-system';
import { useI18n } from '../i18n/index.js';
import { UserMenu } from './UserMenu.js';
import styles from './Topbar.module.css';

export interface TopbarProps {
  /**
   * Breadcrumb segments, e.g. ['TEAM', 'USERS & ROLES']. Already-uppercase
   * is fine; component does not force-case. EN/FR comes from the caller
   * (page-level concern).
   */
  breadcrumb: ReadonlyArray<string>;
  /** Unread notification count; 0 hides the badge. Omit to show no badge. */
  notificationCount?: number;
  /** Click handler for the bell. Opens the notifications panel (out of scope here). */
  onOpenNotifications?: () => void;
}

export function Topbar({ breadcrumb, notificationCount = 0, onOpenNotifications }: TopbarProps) {
  const { t, lang, setLang } = useI18n();

  return (
    <header className={styles.topbar}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        {breadcrumb.map((segment, i) => (
          <span key={`${i}-${segment}`} className={styles.crumb}>
            {i > 0 && <span className={styles.separator} aria-hidden="true">/</span>}
            <span>{segment}</span>
          </span>
        ))}
      </nav>

      <div className={styles.actions}>
        <LangToggle value={lang} onChange={setLang} />

        <button
          type="button"
          className={styles.bellButton}
          aria-label={t('shell.notificationsAriaLabel')}
          onClick={onOpenNotifications}
        >
          <BellIcon width={18} height={18} />
          {notificationCount > 0 && (
            <span className={styles.bellCount} aria-hidden="true">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>

        <UserMenu />
      </div>
    </header>
  );
}
